import {
	set403Error,
	setClientDetails,
	setCurrentUserDetails,
	setPermissions,
	setSecureShipmentDetailsList,
	setUser,
} from "../actions/user";
import { api } from "../config/variables";
import { get } from "../utils/barrels/lodash";
import { createCookie, eraseCookie, reloadUser } from "../utils/functions";
import promiseCache from "../utils/promiseCache";

import { axiosInstance } from "./apiInstance";
import urls from "./urls.json";

export const getConsumerToken = (obj) => {
	const body = JSON.stringify(obj);
	return () =>
		axiosInstance
			.post(urls.getConsumerToken, body)
			.catch((err) => {
				console.error({ err });
				throw err;
			})
			.then((response) => {
				if (response.status === 200 && response.data.token) {
					return response.data.token;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const getClientList =
	(status = "active", search = "", is_on_advanced_plan = false, page = 1) =>
	(dispatch) => {
		const params = {
			status,
			search,
			page,
		};
		if (is_on_advanced_plan) {
			params.rb_plan_name = 1;
		}
		return axiosInstance
			.get(urls.getClientList, {
				params,
			})
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: false,
							showReAuthModal: false,
							requestType: "get",
						})
					);
					window.location.reload();
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200 && response.data?.results) {
					return {
						results: response.data.results,
						count: response.data.count,
						next: response.data.next,
						previous: response.data.previous,
						current_page: response.data.current_page || page,
						total_pages:
							response.data.total_pages || Math.ceil(response.data.count / 10),
					};
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});
	};
export const getUsersByRole = (role) => (dispatch) =>
	axiosInstance
		.get(urls.getUserByRole, {
			params: {
				role,
			},
		})
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			if (errResponse === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: false,
						showReAuthModal: false,
						requestType: "get",
					})
				);
				window.location.reload();
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 200 && response.data) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const getShowFormApi = (params) => (dispatch) =>
	axiosInstance
		.get(urls.getShowForm, {
			params,
		})
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			if (errResponse === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: false,
						showReAuthModal: false,
						requestType: "get",
					})
				);
				window.location.reload();
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 200 && response.data) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const getKycSyncDetailsApi = (params) => (dispatch) =>
	axiosInstance
		.get(urls.getKycSyncDetails, {
			params,
		})
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			if (errResponse === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: false,
						showReAuthModal: false,
						requestType: "get",
					})
				);
				window.location.reload();
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 200 && response.data) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const getClientDetails = (token, expires_in) => {
	const cacheKey = `getClientDetails_${token}_${expires_in}`;
	return (dispatch) => {
		if (promiseCache.isInProgress(cacheKey)) {
			return promiseCache.getExistingPromise(cacheKey);
		}

		const promise = axiosInstance
			.get(urls.getCurrentUser)
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				const errMsg = get(err, "response.data", false);
				if (
					errMsg?.detail !==
						"Please accept agreement terms to proceed further" &&
					errMsg?.detail !== "Auto Assign Carrier is required" &&
					(errResponse === 401 || errResponse === 403)
				) {
					eraseCookie("bearer");
					eraseCookie("expires");
					eraseCookie("client");
					console.error(api, errMsg);
					dispatch(
						set403Error({
							error403: true,
							error403Data: errMsg,
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200 && response.data) {
					dispatch(setPermissions(response.data.permissions));
					const clientData = response.data;
					delete clientData.permissions;
					delete clientData.agreements_accepted;
					delete clientData.groups;
					const clientDetails = JSON.stringify(clientData);
					createCookie(
						"client",
						clientDetails,
						true,
						expires_in,
						"/",
						true,
						true
					);
					setClientDetails({});
					dispatch(
						set403Error({
							error403: false,
							error403Data: {},
						})
					);
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});

		return promiseCache.startCall(cacheKey, promise);
	};
};

export const userKYC = (reqObj) => {
	const body = JSON.stringify(reqObj);
	return (dispatch) =>
		axiosInstance
			.post(urls.createKYC, body)
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "post",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 201 || response.status === 200) {
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const clientSecureShipmentDetails = (reqObj) => {
	const body = JSON.stringify(reqObj);
	return (dispatch) =>
		axiosInstance
			.post(urls.clientSecureShipmentDetails, body)
			.catch((err) => {
				console.error("err", err);
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "post",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 201 || response.status === 200) {
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const getAllClientSecureShipmentDetails =
	(
		// role,
		pageNo,
		query,
		fetchAll
	) =>
	(dispatch) =>
		axiosInstance
			.get(urls.getAllClientSecureShipmentDetails, {
				params: {
					page: pageNo || undefined,
					all_records: fetchAll || undefined,
					q: query || undefined,
				},
			})
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: false,
							showReAuthModal: false,
							requestType: "get",
						})
					);
					window.location.reload();
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200 && response.data) {
					dispatch(setSecureShipmentDetailsList(response.data));
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});

export const getClientSecureShipmentDetails = (client__id) => (dispatch) =>
	axiosInstance
		.get(urls.getClientSecureShipmentDetails, {
			params: {
				client__id,
			},
		})
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			if (errResponse === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: false,
						showReAuthModal: false,
						requestType: "get",
					})
				);
				window.location.reload();
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 200 && response.data) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});
export const getSelectedClientDetails = (client__id) => (dispatch) =>
	axiosInstance
		.get(urls.getSelectedClientDetails, {
			params: {
				client__id,
			},
		})
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			if (errResponse === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: false,
						showReAuthModal: false,
						requestType: "get",
					})
				);
				window.location.reload();
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 200 && response.data) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const profile = (reqObj) => {
	const body = JSON.stringify(reqObj);
	return (dispatch) =>
		axiosInstance
			.put(`${urls.profile}${reqObj.client_id}/`, body)
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({ reAuth: true, showReAuthModal: true, requestType: "put" })
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 201 || response.status === 200) {
					return response;
				} else {
					const err = new Error(response?.data?.msg);
					throw err;
				}
			});
};

export const optSecureShipment = (reqObj) => {
	const body = JSON.stringify(reqObj);
	return (dispatch) =>
		axiosInstance
			.put(`${urls.optSecureShipment}${reqObj.client_id}/`, body)
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({ reAuth: true, showReAuthModal: true, requestType: "put" })
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 201 || response.status === 200) {
					return response;
				} else {
					const err = new Error(response?.data?.msg);
					throw err;
				}
			});
};

export const getKYCList =
	(page = undefined) =>
	(dispatch) =>
		axiosInstance
			.get(urls.getKYCList, {
				params: {
					page,
				},
			})
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: false,
							showReAuthModal: false,
							requestType: "get",
						})
					);
					window.location.reload();
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200) {
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});

export const addClient = (obj) => {
	const body = JSON.stringify(obj);
	return (dispatch) =>
		axiosInstance
			.post(urls.createClient, body)
			.catch((err) => {
				if (err.response.status === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "post",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 201) {
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const userAgreement = (reqObj) => {
	const body = JSON.stringify(reqObj);
	return (dispatch) =>
		axiosInstance
			.post(urls.acceptAgreement, body)
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "post",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 201 || response.status === 200) {
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const UserBankDetails = () => (dispatch) =>
	axiosInstance
		.get(urls.getClientBankDetails)
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			if (errResponse === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: true,
						showReAuthModal: true,
						requestType: "post",
					})
				);
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 201 || response.status === 200) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const getUserCarriers = (client_id) => (dispatch) =>
	axiosInstance
		.get(urls.userCarriers, {
			params: {
				disable_carriers: true,
				client_id,
			},
		})
		.catch((err) => {
			if (err.response.status === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: false,
						showReAuthModal: false,
						requestType: "get",
					})
				);
				window.location.reload();
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 200) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const createDisableCarrier = (obj) => {
	const body = JSON.stringify(obj);
	return (dispatch) =>
		axiosInstance
			.post(urls.userCarriers, body)
			.catch((err) => {
				if (err.response.status === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "post",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if ((response.status === 201) | (response.status === 200)) {
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const deleteDisableCarrier = (obj) => {
	const body = JSON.stringify(obj);
	return (dispatch) =>
		axiosInstance
			.delete(urls.userCarriers, body)
			.catch((err) => {
				if (err.response.status === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "delete",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200) {
					return response.data;
				} else {
					console.error(response);
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const generateApiToken = (obj) => {
	const body = JSON.stringify(obj);
	return (dispatch) =>
		axiosInstance
			.post(urls.generateApiToken, body)
			.catch((err) => {
				if (err.response.status === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "delete",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200) {
					return response.data;
				} else {
					console.error(response);
					const err = new Error(response.data.msg);
					throw err;
				}
			});
};

export const getApiToken = () => (dispatch) =>
	axiosInstance
		.get(urls.generateApiToken)
		.catch((err) => {
			if (err.response.status === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: true,
						showReAuthModal: true,
						requestType: "delete",
					})
				);
			}
			throw err;
		})
		.then((response) => {
			if (response.status === 200) {
				return response.data;
			} else {
				console.error(response);
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const validateCouponApi = (obj) => {
	const body = JSON.stringify(obj);
	return (dispatch) =>
		axiosInstance
			.post(urls.getCoupon, body)
			.catch((err) => {
				if (err.response.status === 401) {
					reloadUser();
					dispatch(
						setUser({
							reAuth: true,
							showReAuthModal: true,
							requestType: "delete",
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200) {
					return response.data;
				} else {
					console.error(response);
					const err = new Error(response);
					throw err;
				}
			});
};

export const getCouponApi = (clientId, token) => (dispatch) =>
	axiosInstance
		.get(urls.getCoupon, {
			params: {
				client_id: clientId,
			},
			headers: {
				Authorization: `token${token}`,
			},
		})
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			if (errResponse === 401) {
				reloadUser();
				dispatch(
					setUser({
						reAuth: true,
						showReAuthModal: true,
						requestType: "delete",
					})
				);
			}
			console.error("coupon ka error", err);
			throw err;
		})
		.then((response) => {
			if (response.status === 200 && response.data) {
				return response.data;
			} else {
				const err = new Error(response.data.msg);
				throw err;
			}
		});

export const getClientUsers = () => {
	const cacheKey = `getClientUsers`;
	return (dispatch) => {
		if (promiseCache.isInProgress(cacheKey)) {
			return promiseCache.getExistingPromise(cacheKey);
		}

		const promise = axiosInstance
			.get(urls.getClientUsers)
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				const errMsg = get(err, "response.data", false);
				if (
					errMsg?.detail !==
						"Please accept agreement terms to proceed further" &&
					errMsg?.detail !== "Auto Assign Carrier is required" &&
					(errResponse === 401 || errResponse === 403)
				) {
					eraseCookie("bearer");
					eraseCookie("expires");
					eraseCookie("client");
					console.error(api, errMsg);
					dispatch(
						set403Error({
							error403: true,
							error403Data: errMsg,
						})
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 200 && response.data) {
					return response.data;
				} else {
					const err = new Error(response.data.msg);
					throw err;
				}
			});

		return promiseCache.startCall(cacheKey, promise);
	};
};

export const postQuickThreeWheelerLogApi = (reqObj) => {
	const body = JSON.stringify(reqObj);
	return (dispatch) =>
		axiosInstance
			.post(urls.quickLogApi, body)
			.catch((err) => {
				const errResponse = get(err, "response.status", false);
				if (errResponse === 401) {
					reloadUser();
					dispatch(
						setUser({ reAuth: true, showReAuthModal: true, requestType: "put" })
					);
				}
				throw err;
			})
			.then((response) => {
				if (response.status === 201 || response.status === 200) {
					return response;
				} else {
					const err = new Error(response?.data?.msg);
					throw err;
				}
			});
};

export const getCurrentUserDetails = () => (dispatch) => {
	dispatch(setCurrentUserDetails({ loading: true, error: null }));

	return axiosInstance
		.get(urls.getCurrentUserDetails)
		.catch((err) => {
			const errResponse = get(err, "response.status", false);
			const errMsg = get(err, "response.data", false);

			if (
				errMsg?.detail !== "Please accept agreement terms to proceed further" &&
				errMsg?.detail !== "Auto Assign Carrier is required" &&
				(errResponse === 401 || errResponse === 403)
			) {
				eraseCookie("bearer");
				eraseCookie("expires");
				eraseCookie("client");
				console.error(api, errMsg);
				dispatch(
					set403Error({
						error403: true,
						error403Data: errMsg,
					})
				);
			}

			dispatch(
				setCurrentUserDetails({
					loading: false,
					error: errMsg || "Failed to fetch user details",
				})
			);
			throw err;
		})
		.then((response) => {
			if (response.status === 200 && response.data) {
				dispatch(
					setCurrentUserDetails({
						data: response.data,
						loading: false,
						error: null,
					})
				);
				return response.data;
			} else {
				const err = new Error(
					response.data.msg || "Failed to fetch user details"
				);
				dispatch(
					setCurrentUserDetails({
						loading: false,
						error: err.message,
					})
				);
				throw err;
			}
		});
};

/**
 * Direct API call to get current user details without Redux
 * @returns {Promise<Object>} User details from API
 */
export const fetchCurrentUserDetailsWithoutRedux = async () => {
	try {
		const response = await axiosInstance.get(urls.getCurrentUserDetails);
		if (response.status === 200 && response.data) {
			return response.data;
		} else {
			throw new Error(response.data?.msg || "Failed to fetch user details");
		}
	} catch (error) {
		console.error("Error fetching current user details:", error);
		throw error;
	}
};
